/**
 * 🆕 [Phase 56 BT — P2.2 refactor] Dialog de confirmation « Conflit de congé détecté »
 *
 * Extrait de `EditerBonTravailDialog.jsx` (L3091-3130) pour alléger le parent.
 * Affiché quand l'utilisatrice tente d'enregistrer un BT pendant la période de
 * congé approuvé d'un ou plusieurs employés assignés.
 *
 * Props :
 * - `open` : bool
 * - `onClose` : () => void — fermer sans enregistrer (annuler)
 * - `onForceConfirm` : async () => void — forcer l'enregistrement quand même
 * - `leaveConflicts` : [{employe_nom, employee_nom, date_debut, date_fin}]
 *
 * Bug 4 (Phase 51 BIS) : ce dialog DOIT rester fonctionnel après refactor.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';


export function LeaveConflictDialog({ open, onClose, onForceConfirm, leaveConflicts }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-confirm-conge-edit">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Conflit de congé détecté
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            {leaveConflicts.length === 1
              ? 'Un employé assigné est en congé approuvé pendant la période :'
              : `${leaveConflicts.length} employés assignés sont en congé approuvé pendant la période :`}
          </p>
          <ul className="space-y-1 bg-red-50 border border-red-200 rounded-lg p-3">
            {leaveConflicts.map((c, i) => (
              <li key={c.id || c._id || i} className="text-sm text-red-800">
                <strong>{c.employe_nom || c.employee_nom}</strong>
                <span className="text-red-600 text-xs ml-1">
                  (du {c.date_debut} au {c.date_fin})
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 italic">
            Voulez-vous quand même enregistrer ce BT ? Cette action sera enregistrée telle quelle.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="btn-confirm-conge-cancel-edit">
              Annuler et modifier
            </Button>
            <Button
              onClick={onForceConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-conge-force-edit"
            >
              Enregistrer quand même
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * 🆕 [SESSION MAX SUITE 2 — P2.4] Bandeau d'avertissement inline pour les
 * conflits de congé approuvé pendant la période du chantier.
 *
 * À ne pas confondre avec `LeaveConflictDialog` qui est la modale de
 * confirmation au moment d'enregistrer le BT. Ce composant est un simple
 * encart rouge inline dans le formulaire qui informe l'utilisatrice qu'au moins
 * un employé assigné est en congé. Aucun bouton — juste un warning visuel.
 *
 * Props :
 * - `leaveConflicts` : [{employe_nom | employee_nom, date_debut, date_fin}]
 *   Si vide, le composant ne rend rien.
 */
export function LeaveConflictWarningInline({ leaveConflicts }) {
  if (!leaveConflicts || leaveConflicts.length === 0) return null;
  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3" data-testid="leave-conflicts-warning-edit">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
        <p className="text-sm font-bold text-red-900">
          ⚠️ Conflit de congé détecté
        </p>
      </div>
      <div className="space-y-1">
        {leaveConflicts.map((c, i) => (
          <p key={c.id || c._id || i} className="text-xs text-red-800 flex items-center gap-1">
            <span>•</span>
            <span className="font-semibold">{c.employe_nom || c.employee_nom}</span>
            <span className="text-red-600">en congé du {c.date_debut} au {c.date_fin}</span>
          </p>
        ))}
      </div>
      <p className="text-xs text-red-700 italic mt-2">Retirez cet employé de l&apos;équipe ou modifiez les dates du chantier.</p>
    </div>
  );
}


export function ZonePhotoFullscreenDialog({ zone, onClose, apiUrl }) {
  if (!zone) return null;
  const src = zone.url?.startsWith('http') ? zone.url : `${apiUrl}${zone.url}`;
  return (
    <Dialog open={!!zone} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto p-4"
        data-testid="zone-photo-fullscreen-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <span className="bg-green-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">
              Z{zone.numero}
            </span>
            <span className="break-words">{zone.nom || `Zone ${zone.numero}`}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <img
            src={src}
            alt={zone.nom || `Zone ${zone.numero}`}
            className="w-full max-h-[70vh] object-contain rounded-lg bg-slate-100"
            data-testid="zone-photo-fullscreen-img"
          />
          {zone.description && zone.description.trim() && (
            <div className="p-3 bg-slate-50 border rounded-lg">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">
                Détails de la zone
              </p>
              <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">
                {zone.description}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
